import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import packageJson from '../../package.json';
import * as ConfigValues from '../util/configValues';

let isSentryInitialized = false;

export interface SentryConfig {
  isExtendedTrackingEnabled: boolean; // Maps to IsEnabled from backend
  replayEnabled: boolean; // Maps to IsReplayEnabled from backend
  userIdTracing: boolean; // Maps to IsUserIdTracingEnabled from backend
  isEnhancedDiagnosticsEnabled: boolean;
  userId?: string;
}

/**
 * Initialisiert Sentry with DSGVO Settings
 */
export const initializeSentry = (config: SentryConfig) => {
  if (isSentryInitialized) {
    console.warn('Sentry already initialized');
    return;
  }

  if (ConfigValues.REACT_APP_API_REDIRECT_URL.includes('prio.local')) {
    console.info('Sentry disabled for local environment');
    return;
  }

  const {
    isExtendedTrackingEnabled,
    replayEnabled,
    userIdTracing,
    isEnhancedDiagnosticsEnabled,
    userId,
  } = config;

  console.log('[Sentry] Initializing with config:', {
    isExtendedTrackingEnabled,
    replayEnabled,
    userIdTracing,
    isEnhancedDiagnosticsEnabled,
    hasUserId: !!userId,
  });

  Sentry.setContext('customer', {
    domain: ConfigValues.REACT_APP_API_REDIRECT_URL,
  });

  Sentry.init({
    dsn: 'https://9f77435a9559fadc91ffcfc8737afe67@sentry.prio365.app/5',
    integrations: [
      Sentry.captureConsoleIntegration({
        levels: ['error'],
      }),
      ...(isExtendedTrackingEnabled
        ? (console.debug('[Sentry] Browser Tracing & Router Tracing enabled'),
          [
            Sentry.browserTracingIntegration(),
            Sentry.reactRouterV6BrowserTracingIntegration({
              useEffect,
              useLocation,
              useNavigationType,
              createRoutesFromChildren,
              matchRoutes,
            }),
          ])
        : (console.debug('[Sentry] Browser Tracing disabled'), [])),
      ...(replayEnabled
        ? (console.debug('[Sentry] Session Replay enabled (masked)'),
          [
            Sentry.replayIntegration({
              maskAllText: true,
              blockAllMedia: true,
            }),
          ])
        : (console.debug('[Sentry] Session Replay disabled'), [])),
    ],
    release: packageJson.version,
    autoSessionTracking: false,
    environment: ConfigValues.REACT_APP_API_REDIRECT_URL.includes('prio.local')
      ? 'local'
      : ConfigValues.REACT_APP_API_REDIRECT_URL.includes(
            'fc-digitalisierung.de'
          )
        ? 'dev'
        : ConfigValues.REACT_APP_API_REDIRECT_URL.includes('fc-coding.de')
          ? 'qa'
          : ConfigValues.REACT_APP_API_REDIRECT_URL.includes('prio365.app')
            ? 'staging'
            : 'production',

    sendDefaultPii: false,

    beforeSend(event) {
      if (event.user) {
        delete event.user.ip_address;
        delete event.user.geo;
      }
      if (event.request) {
        delete event.request.env;
      }

      if (userIdTracing && userId) {
        console.debug('[Sentry] Adding User ID to event:', userId);
        event.user = {
          ...event.user,
          id: userId,
        };
      } else {
        console.debug(
          '[Sentry] User ID tracking disabled or no userId available'
        );
      }

      return event;
    },

    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.category === 'xhr' || breadcrumb.category === 'fetch') {
        if (breadcrumb.data?.url) {
          try {
            const url = new URL(breadcrumb.data.url);
            breadcrumb.data.url = url.origin + url.pathname;
          } catch {}
        }
      }

      if (!isEnhancedDiagnosticsEnabled) {
        if (
          breadcrumb.category === 'ui.click' ||
          breadcrumb.category === 'ui.input'
        ) {
          console.debug(
            `[Sentry] Filtering ${breadcrumb.category} breadcrumb (Enhanced Diagnostics disabled)`
          );
          return null;
        }
      }

      return breadcrumb;
    },

    tracesSampleRate: isExtendedTrackingEnabled ? 0.5 : 0,

    replaysSessionSampleRate: replayEnabled ? 0.1 : 0,
    replaysOnErrorSampleRate: replayEnabled ? 1.0 : 0,

    maxBreadcrumbs: isEnhancedDiagnosticsEnabled ? 100 : 50,

    denyUrls: [],
    allowUrls: [],

    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications.',
      "Error: Connection disconnected with error 'Error: WebSocket closed with status code: 1006 ().'",
      "Error: Connection disconnected with error 'Error: Server returned an error on close: Connection closed with an error.",
      'uuidv4() is deprecated. Use v4() from the uuid module instead.',
      'Error: Failed to start the connection: Error',
      'Error: Failed to complete negotiation with the server: Error',
      "Error: Connection disconnected with error 'Error: Server timeout elapsed without receiving a message from the server.'",
      'Error: Failed to start the connection: TypeError: Failed to fetch',
      'Error: Failed to complete negotiation with the server: TypeError: Failed to fetch',
      'Error: Failed to complete negotiation with the server: Error: Unauthorized',
      'Error: Failed to start the connection: Error: Unauthorized',
      'Error: Server returned handshake error: Handshake was canceled.',
      "Error: Connection disconnected with error 'Error: Server returned handshake error: Handshake was canceled.'",
      "Error: Failed to start the transport 'WebSockets': Error: There was an error with the transport.",
      "Error: Failed to start the transport 'ServerSentEvents': Error: Error occurred",
      'BrowserAuthError: user_cancelled: User cancelled the flow.',
    ],
  });

  isSentryInitialized = true;

  const activeFeatures = [
    isExtendedTrackingEnabled && '✓ Extended Tracking (tracesSampleRate: 0.5)',
    replayEnabled && '✓ Session Replay (0.1 session / 1.0 error)',
    userIdTracing && '✓ User ID Tracking',
    isEnhancedDiagnosticsEnabled && '✓ Enhanced Diagnostics (100 breadcrumbs)',
  ].filter(Boolean);

  console.info(
    `[Sentry] ✓ Initialized successfully\n` +
      `Environment: ${ConfigValues.REACT_APP_API_REDIRECT_URL.includes('prio.local') ? 'local' : ConfigValues.REACT_APP_API_REDIRECT_URL.includes('fc-digitalisierung.de') ? 'dev' : ConfigValues.REACT_APP_API_REDIRECT_URL.includes('fc-coding.de') ? 'qa' : ConfigValues.REACT_APP_API_REDIRECT_URL.includes('prio365.app') ? 'staging' : 'production'}\n` +
      `Version: ${packageJson.version}\n` +
      `Active Features:\n${activeFeatures.length > 0 ? activeFeatures.join('\n') : '✗ All features disabled'}\n` +
      `Max Breadcrumbs: ${isEnhancedDiagnosticsEnabled ? 100 : 50}`
  );
};

export const updateSentryUser = (userId: string | undefined) => {
  if (!isSentryInitialized) {
    console.warn('[Sentry] Cannot update user - Sentry not initialized');
    return;
  }

  if (userId) {
    console.log('[Sentry] Updating user context with ID:', userId);
    Sentry.setUser({
      id: userId,
    });
  } else {
    console.log('[Sentry] Clearing user context');
    Sentry.setUser(null);
  }
};

export const isSentryReady = () => isSentryInitialized;
